/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import styled, { css } from "styled-components";

// Base properties for all buttons
const ButtonBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "IBM Plex Mono", monospace;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  white-space: nowrap;
`;

export const Button = styled(ButtonBase)<{ $disabled?: boolean; $dangerous?: boolean }>`
  line-height: 23px;
  font-size: 15px;
  padding: 2px 20px 2px 20px;
  background-color: ${({ theme }) => theme.button.default};
  display: inline-block;
  ${({ $disabled, $dangerous, theme }) => {
    if ($disabled) {
      return css`
        border: 1px solid ${theme.button.disabled};
        box-shadow: 2px 2px ${theme.button.disabled};
        color: ${theme.button.disabled};
      `;
    }
    if ($dangerous) {
      return css`
        border: 1px solid ${theme.accent};
        box-shadow: 2px 2px ${theme.accent};
        color: ${theme.accent};
      `;
    }
    return css`
      border: 1px solid ${theme.primary};
      box-shadow: 2px 2px ${theme.primary};
      color: ${theme.primary};
    `;
  }}
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  ${({ $disabled }) =>
    !$disabled &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.button.hover};
      }
      &:active {
        background-color: ${({ theme }) => theme.button.active};
        box-shadow: none;
        position: relative;
        left: 2px;
        top: 2px;
      }
    `}
`;

export const ButtonSmall = styled(ButtonBase)`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  padding: 2px 20px;
  background-color: ${({ theme }) => theme.button.default};
  color: ${({ theme }) => theme.text.default};
  border: ${({ theme }) => `1px solid ${theme.primary}`};
  box-shadow: ${({ theme }) => `2px 2px ${theme.primary}`};
  user-select: none;
  &:hover {
    background-color: ${({ theme }) => theme.button.hover};
  }
  &:active {
    background-color: ${({ theme }) => theme.button.active};
    box-shadow: none;
    position: relative;
    left: 2px;
    top: 2px;
  }
`;

export const HighContrastButton = styled(ButtonBase)<{ $disabled?: boolean }>`
  line-height: 23px;
  font-size: 15px;
  padding: 2px 20px;
  ${({ $disabled }) =>
    $disabled
      ? css`
          background-color: ${({ theme }) => theme.background};
          border: ${({ theme }) => `1px solid ${theme.button.disabled}`};
          box-shadow: ${({ theme }) => `2px 2px ${theme.button.disabled}`};
          cursor: not-allowed;
          color: ${({ theme }) => theme.button.disabled};
        `
      : css`
          background-color: ${({ theme }) => theme.highContrastButton.default};
          border: ${({ theme }) => `1px solid ${theme.highContrastButton.default}`};
          box-shadow: 2px 2px #bdbdbd;
          cursor: pointer;
          color: ${({ theme }) => theme.button.default};
          &:hover {
            background-color: ${({ theme }) => theme.highContrastButton.hover};
          }
          &:active {
            background-color: ${({ theme }) => theme.highContrastButton.active};
            box-shadow: none;
            position: relative;
            left: 2px;
            top: 2px;
          }
        `}
`;

export const HighContrastButtonSmall = styled(ButtonBase)`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  padding: 2px 20px;
  background-color: ${({ theme }) => theme.highContrastButton.default};
  color: ${({ theme }) => theme.button.default};
  border: ${({ theme }) => `1px solid ${theme.highContrastButton.default}`};
  box-shadow: 2px 2px 0px #904d4d;
  &:hover {
    background-color: ${({ theme }) => theme.highContrastButton.hover};
  }
  &:active {
    background-color: ${({ theme }) => theme.highContrastButton.active};
    box-shadow: none;
    position: relative;
    left: 2px;
    top: 2px;
  }
`;
